/**
 * Initialise the news details page table of contents.
 */
function initNewsDetailsPage() {
    const toc = document.querySelector('.content-page__table-content');

    const tocHeader = toc?.querySelector(
        '.content-page__table-content-title'
    );

    const tocPanel = tocHeader?.nextElementSibling;

    const content = document.querySelector('.content-page__table-content-text');

    const tocListContainer = document.getElementById('tbl_content');

    if (!toc || !tocHeader || !tocPanel || !content || !tocListContainer) {
        return;
    }

    const desktopBreakpoint = 1299;
    let fixedTopSpacing = 30;

    let tocOriginalTop = 0;
    let ticking = false;

    /*
     * Prevent duplicate TOC items if this initializer
     * is called more than once.
     */
    tocListContainer.replaceChildren();

    buildTableOfContents(content, tocListContainer, toc);

    if (toc.style.display === 'none') {
        return;
    }

    /**
     * Returns true when the desktop layout is active.
     */
    function isDesktop() {
        return window.innerWidth >= desktopBreakpoint;
    }

    /**
     * On desktop, the panel is always open.
     * On mobile, it behaves like an accordion.
     */
    function updateAccordionState() {
        if (isDesktop()) {
            tocPanel.style.display = 'block';

            tocHeader.classList.remove('active');

            tocHeader.setAttribute('aria-expanded', 'true');
            tocHeader.setAttribute('aria-disabled', 'true');

            return;
        }

        tocHeader.removeAttribute('aria-disabled');

        const isOpen = tocHeader.classList.contains('active');

        tocPanel.style.display = isOpen ? 'block' : 'none';

        tocHeader.setAttribute('aria-expanded', String(isOpen));
    }

    /**
     * Accordion click behaviour is enabled only
     * below the desktop breakpoint.
     */
    tocHeader.addEventListener('click', () => {
        if (isDesktop()) {
            return;
        }

        const isOpen = tocHeader.classList.toggle('active');

        tocPanel.style.display = isOpen ? 'block' : 'none';

        tocHeader.setAttribute('aria-expanded', String(isOpen));

        window.requestAnimationFrame(() => {
            updateTocPosition();
        });
    });

    /**
     * Generate the table of contents from headings.
     */
    function buildTableOfContents(contentElement, listContainer, tocElement) {
        const headings = contentElement.querySelectorAll(
            'h1, h2, h3, h4, h5, h6'
        );
        if (!headings.length) {
            tocElement.style.display = 'none';
            return;
        }

        const list = document.createElement('ul');
        let headingIndex = 0;
        headings.forEach((heading) => {
            headingIndex++;
            if (!heading.id) {
                heading.id = `heading-${headingIndex}`;
            }

            heading.dataset.id = heading.localName;

            const level = Number.parseInt(
                heading.tagName.substring(1),
                10
            );

            const listItem = document.createElement('li');
            const link = document.createElement('a');

            listItem.style.setProperty('--toc-level', Math.max(level - 1, 0));

            link.href = `#${heading.id}`;
            link.textContent = heading.textContent.trim();

            link.classList.add(`content-page__table-content-heading-${heading.localName}`);

            listItem.appendChild(link);
            list.appendChild(listItem);
        });

        listContainer.appendChild(list);
    }

    /**
     * Calculate the TOC's original position.
     */
    function calculateTocOriginalPosition() {
        const wasFixed = toc.classList.contains('is-fixed');

        if (wasFixed) {
            toc.classList.remove('is-fixed');

        }

        tocOriginalTop = toc.getBoundingClientRect().top + window.scrollY;

        updateTocPosition();
    }

    /**
     * Fix the TOC to the left side on desktop after
     * scrolling past its original position.
     */
    function updateTocPosition() {
        const articleRect =
            content.getBoundingClientRect();

        const articleBottom =
            articleRect.bottom + window.scrollY;

        const tocHeight =
            toc.offsetHeight;

        const fixedTocBottom =
            window.scrollY +
            fixedTopSpacing +
            tocHeight;

        const hasReachedArticleEnd =
            fixedTocBottom >= articleBottom;

        const shouldUseFixedPosition =
            isDesktop();

        const shouldHideToc =
            shouldUseFixedPosition &&
            hasReachedArticleEnd;

        toc.classList.toggle(
            'is-fixed',
            shouldUseFixedPosition
        );

        toc.classList.toggle(
            'is-hidden',
            shouldHideToc
        );
    }

    /**
     * Prevent anchored headings from being hidden
     * behind the website header.
     */
    function adjustScrollMargin() {
        const header = document.querySelector('header');
        const spacingBelowHeader = 20;

        let scrollMargin = spacingBelowHeader;

        if (header) {
            const headerStyles =
                window.getComputedStyle(header);

            const hasStickyHeader =
                headerStyles.position === 'fixed' ||
                headerStyles.position === 'sticky';

            if (hasStickyHeader) {
                scrollMargin =
                    header.getBoundingClientRect().height +
                    spacingBelowHeader;
            }
        }

        const headings =
            content.querySelectorAll('[id]');

        headings.forEach((heading) => {
            heading.style.scrollMarginTop =
                `${scrollMargin}px`;
        });
    }

    /**
     * Throttle scroll events.
     */
    function handleScroll() {
        if (ticking) {
            return;
        }

        ticking = true;

        window.requestAnimationFrame(() => {
            updateFixedTop();
            updateTocPosition();

            ticking = false;
        });
    }

    /**
     * Recalculate state after resizing.
     */
    function handleResize() {
        toc.classList.remove(
            'is-fixed',
            'is-hidden'
        );

        updateAccordionState();

        window.requestAnimationFrame(() => {
            adjustScrollMargin();
            updateFixedTop();
            updateTocPosition();
        });
    }

    // Calculate the TOC's original position and update the fixed top spacing.
    function updateFixedTop() {
        const header = document.querySelector('header');
        const spacingBelowHeader = 20;

        const contentTop =
            content.getBoundingClientRect().top;

        let minimumTopSpacing = spacingBelowHeader;

        if (header) {
            const headerStyles =
                window.getComputedStyle(header);

            const headerPosition =
                headerStyles.position;

            const hasStickyHeader =
                headerPosition === 'fixed' ||
                headerPosition === 'sticky';

            if (hasStickyHeader) {
                const headerRect =
                    header.getBoundingClientRect();

                /*
                 * Use the visible bottom edge of a sticky/fixed header.
                 */
                minimumTopSpacing =
                    Math.max(
                        headerRect.bottom,
                        0
                    ) + spacingBelowHeader;
            }
        }

        /*
         * Initially align with the article content.
         * While scrolling, stop at either:
         * - below the sticky/fixed header, or
         * - 20px from the top when there is no sticky header.
         */
        fixedTopSpacing = Math.max(
            contentTop,
            minimumTopSpacing
        );

        toc.style.setProperty(
            '--toc-top',
            `${fixedTopSpacing}px`
        );
    }
    window.addEventListener('scroll', handleScroll, { passive: true });

    window.addEventListener('resize', handleResize);

    window.addEventListener('load', handleResize);

    updateAccordionState();
    adjustScrollMargin();
    updateFixedTop();
    updateTocPosition();
}

initNewsDetailsPage();

export default initNewsDetailsPage;